import { useCallback, useRef } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { isDocumentTitle } from '@cotera/client/app/components/ui/tiptap/tiptap-ui/turn-into-dropdown/use-turn-into-dropdown';
import { useDebouncedDirtySave } from '@cotera/client/app/hooks/use-debounced-dirty-save';
import type { DG } from '@cotera/sdk/core';

function extractTitleFromDocState(
  state: DG.DocumentEditor.DocumentState
): string | null {
  const content = state.content;
  if (content === undefined || content.length === 0) {
    return null;
  }
  const first = content[0];
  if (
    first === undefined ||
    first.type !== 'heading' ||
    first.attrs?.['level'] !== 1
  ) {
    return null;
  }
  const textNode = first.content?.[0];
  if (textNode === undefined || textNode.type !== 'text') {
    return null;
  }
  const text = (textNode as { text?: string }).text;
  return text?.trim() ?? null;
}

type UseContinualSaveParams = {
  save: () => void;
  getEditorStateForSave: () => DG.DocumentEditor.DocumentState;
  /** When false, skips autosave (e.g. when agent is locked by Coco) */
  enabled?: boolean;
};

/**
 * Hook for continual autosave that only runs when:
 * - There is a non-empty title (agent needs a name before it can be saved)
 * - User has moved focus away from the title (to content or elsewhere)
 * - enabled is true (e.g. agent is not locked)
 *
 * Call scheduleSave(editor) from onUpdate when the user makes changes.
 */
export function useContinualSave({
  save,
  getEditorStateForSave,
  enabled = true,
}: UseContinualSaveParams) {
  const editorRef = useRef<TiptapEditor | null>(null);

  const { scheduleSave: scheduleBaseSave } = useDebouncedDirtySave({
    save,
    enabled,
    canSave: () => {
      const editorState = getEditorStateForSave();
      const title = extractTitleFromDocState(editorState);
      if (title === null || title === '') {
        return false;
      }
      const editor = editorRef.current;
      if (editor !== null && editor.isFocused && isDocumentTitle(editor)) {
        return false;
      }
      return true;
    },
  });

  const scheduleSave = useCallback(
    (editor: TiptapEditor) => {
      editorRef.current = editor;
      scheduleBaseSave();
    },
    [scheduleBaseSave]
  );

  return { scheduleSave };
}
